/** Utility functions for resolving key anatomical positions on the player sprite (fist, body center, etc.). */
import { ConfigLoader } from '../systems/ConfigLoader';
import { magnitude } from './Math';
import type { Position } from './Types';

export interface FistPosition {
  /** World x of the fist. */
  x: number;
  /** World y of the fist. */
  y: number;
  /** Normalized facing x component. */
  nx: number;
  /** Normalized facing y component. */
  ny: number;
}

/**
 * Returns the world position of the player's fist given the sprite's world position and facing direction.
 * Accounts for per-cardinal nudges so the fist tracks the punch animation for all four directions.
 */
export function getFistPosition(spriteX: number, spriteY: number, facing: Position): FistPosition {
  const offset = ConfigLoader.combat.hitbox.offset * 0.75;
  const len = magnitude(facing.x, facing.y);
  const nx = len > 0 ? facing.x / len : 0;
  const ny = len > 0 ? facing.y / len : 1;

  const isHorizontal = Math.abs(nx) > Math.abs(ny);
  const yNudge = isHorizontal ? (nx > 0 ? -12 : -12) : (ny > 0 ? 2 : -3);
  const xNudge = !isHorizontal ? (ny > 0 ? 1 : -7) : 0;
  const forwardScale = isHorizontal ? 2.0 : 1.8;

  return {
    x: spriteX + nx * offset * forwardScale + xNudge,
    y: spriteY + ny * offset * forwardScale + yNudge,
    nx,
    ny,
  };
}
